import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputAdornment,
  Radio,
  RadioGroup,
  Switch,
  TextField,
} from "@mui/material";
import { Transition } from "../../pages/app/utils/animation";
import { useModals } from "../../store/dialogs/hooks";
import { Controller, useForm } from "react-hook-form";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ASI_FILTER_TAP_NUMBER } from "../../pages/app/utils/constants";

const ASISchema = (sampleRate) => {
  const maxNumTaps = sampleRate / 2;

  return z.object({
    stepSize: z.preprocess(
      (val) => (val === "" ? undefined : val),
      z.coerce
        .number({
          message: "Please enter a valid number.",
        })
        .min(0.0001, "Must be between 0.0001 and 0.1.")
        .max(0.1, "Must be between 0.0001 and 0.1.")
        .refine((val) => Number(val.toFixed(4)) === val, {
          message: "Maximum 4 decimal places allowed.",
        }),
    ),
    highCutFreq: z.preprocess(
      (val) => (val === "" ? undefined : val),
      z.coerce
        .number({
          message: "Please enter a valid number.",
        })
        .min(0, "Must be at least 0."),
    ),
    lowCutFreq: z.preprocess(
      (val) => (val === "" ? undefined : val),
      z.coerce
        .number({
          message: "Please enter a valid number.",
        })
        .min(0, "Must be at least 0."),
    ),
    gaindB: z.preprocess(
      (val) => (val === "" ? undefined : val),
      z.coerce
        .number({
          message: "Please enter a valid number.",
        })
        .min(0, "Must be at least 0."),
    ),
    attenuationdB: z.preprocess(
      (val) => (val === "" ? undefined : val),
      z.coerce
        .number({
          message: "Please enter a valid number.",
        })
        .lt(0, "Must be less than 0")
        .min(-199, "Must be less than 200"),
    ),
    numTaps: z
      .union([
        z.string(),
        z.number(),
        z.object({
          id: z.number().optional(),
          label: z.string(),
          value: z.number(),
        }),
      ])
      .nullable()
      .superRefine((val, ctx) => {
        if (val === null || val === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Please select or enter a valid number.",
          });
          return;
        }

        const rawVal = val?.value ?? val;
        const num = Number(rawVal);

        if (isNaN(num) || String(rawVal).trim() === "") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Please enter a valid number.",
          });
          return;
        }
        if (num < 65) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Value must be at least 65.",
          });
        }
        if (num >= maxNumTaps) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Value must be less than ${maxNumTaps}.`,
          });
        }
      }),
    adaptFlag: z.union([z.literal(0), z.literal(1)]),
    adaptMethodFlag: z.union([z.literal(0), z.literal(1)]),
    specTarget: z.union([
      z.literal(0),
      z.literal(1),
      z.literal(2),
      z.literal(3),
      z.literal(4),
      z.literal(5),
    ]),
  });
};
export const ASIFilterDialog = ({ onRun, sampleRate }) => {
  const { closeASIFilterDialog, showASIFilterDialog } = useModals();

  const {
    control,
    handleSubmit,
    reset: rhfReset,
    formState: { errors, isDirty, isValid },
    trigger,
  } = useForm({
    resolver: zodResolver(ASISchema(sampleRate)),
    defaultValues: {
      stepSize: 0.02,
      highCutFreq: 5500,
      lowCutFreq: 250,
      gaindB: 0,
      attenuationdB: -40,
      numTaps: ASI_FILTER_TAP_NUMBER[1],
      adaptFlag: 1,
      adaptMethodFlag: 0,
      specTarget: 0,
    },
    mode: "all",
  });

  const handleClose = () => {
    rhfReset();
    closeASIFilterDialog();
  };

  const onSubmit = (data) => {
    onRun({
      ...data,
      numTaps: Number(data.numTaps?.value ?? data.numTaps),
    });
    rhfReset();
    handleClose();
  };

  return (
    <Dialog
      aria-hidden={!showASIFilterDialog}
      open={showASIFilterDialog}
      onClose={handleClose}
      closeAfterTransition={false}
      slots={{
        transition: Transition,
      }}
    >
      <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>ASI</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 4 }}>
            Please edit the settings below as needed to run the ASI filter. If
            you are unsure of the settings, try using the default values.
          </DialogContentText>
          <Grid container columnSpacing={2} rowSpacing={4}>
            <Grid size={{ xs: 4 }}>
              <Controller
                name="stepSize"
                control={control}
                defaultValue={0.0001}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Step Size"
                    type="number"
                    fullWidth
                    helperText={
                      errors.stepSize
                        ? errors.stepSize?.message
                        : "The step size should be between 0.0001 and 0.1."
                    }
                    error={!!errors.stepSize}
                    slotProps={{
                      htmlInput: { min: 0.0001, max: 0.1, step: 0.0001 },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Controller
                name="highCutFreq"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="High Cut Frequency"
                    helperText={
                      errors.highCutFreq
                        ? errors.highCutFreq.message
                        : "The high cut frequency range."
                    }
                    error={!!errors.highCutFreq}
                    slotProps={{
                      htmlInput: { min: 0, step: 1 },
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">Hz</InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Controller
                name="lowCutFreq"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Low Cut Frequency"
                    helperText={
                      errors.lowCutFreq
                        ? errors.lowCutFreq.message
                        : "The low cut frequency range."
                    }
                    error={!!errors.lowCutFreq}
                    slotProps={{
                      htmlInput: { min: 0, step: 1 },
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">Hz</InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Controller
                name="gaindB"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Gain"
                    type="number"
                    helperText={
                      errors.gaindB
                        ? errors.gaindB.message
                        : "The output gain of the filtered signal."
                    }
                    error={!!errors.gaindB}
                    slotProps={{
                      htmlInput: { min: 0, step: 1 },
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">dB</InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Controller
                name="attenuationdB"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Attenuation"
                    type="number"
                    helperText={
                      errors.attenuationdB
                        ? errors.attenuationdB.message
                        : "The maximum attenuation of the frequency response."
                    }
                    error={!!errors.attenuationdB}
                    slotProps={{
                      htmlInput: { min: 0, step: 1 },
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">dB</InputAdornment>
                        ),
                      },
                    }}
                    sx={{
                      "& .MuiFormHelperText-root": {
                        minHeight: 60,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Controller
                name="numTaps"
                control={control}
                render={({ field: { onChange, value, onBlur, ref } }) => (
                  <Autocomplete
                    freeSolo
                    options={ASI_FILTER_TAP_NUMBER.filter(
                      (option) => option.value < sampleRate / 2,
                    )}
                    value={value}
                    getOptionLabel={(option) => {
                      if (typeof option === "object")
                        return option?.label ?? "";
                      return String(option ?? "");
                    }}
                    onChange={(_, newValue) => {
                      onChange(newValue);
                      trigger("numTaps");
                    }}
                    onInputChange={(_, newInputValue) => {
                      if (!newInputValue.trim()) {
                        onChange(null);
                        return;
                      }
                      if (
                        typeof value === "object" &&
                        value?.label === newInputValue
                      ) {
                        return;
                      }
                      onChange(newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputRef={ref}
                        onBlur={onBlur}
                        label="Filter Length (samples)"
                        variant="outlined"
                        error={!!errors.numTaps}
                        helperText={
                          errors.numTaps
                            ? errors.numTaps.message
                            : "The number of audio samples in the filter."
                        }
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <FormControl
                component="legend"
                variant="standard"
                sx={{ gap: 0.5 }}
              >
                <FormLabel
                  component="legend"
                  sx={{
                    "&.Mui-focused:not(.Mui-error)": {
                      color: "text.secondary",
                    },
                  }}
                >
                  Adapt
                </FormLabel>
                <Controller
                  name="adaptFlag"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <FormControlLabel
                      label={value === 0 ? "Freeze" : "Adapt"}
                      control={
                        <Switch
                          {...field}
                          checked={value === 1}
                          onChange={(e) => onChange(e.target.checked ? 1 : 0)}
                        />
                      }
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl
                component="legend"
                variant="standard"
                sx={{ gap: 0.5 }}
              >
                <FormLabel
                  component="legend"
                  sx={{
                    "&.Mui-focused:not(.Mui-error)": {
                      color: "text.secondary",
                    },
                  }}
                >
                  Adapt Method
                </FormLabel>
                <Controller
                  name="adaptMethodFlag"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <FormControlLabel
                      label={
                        value === 0
                          ? "Continuous adaptation"
                          : "VAD triggered adaptation"
                      }
                      control={
                        <Switch
                          {...field}
                          checked={value === 1}
                          onChange={(e) => onChange(e.target.checked ? 1 : 0)}
                        />
                      }
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 8 }}>
              <Controller
                name="specTarget"
                control={control}
                render={({ field }) => (
                  <FormControl
                    error={!!errors.specTarget}
                    component="fieldset"
                    sx={{ gap: 0.5 }}
                  >
                    <FormLabel
                      id="specTarget-radio-buttons-group-label"
                      sx={{
                        "&.Mui-focused:not(.Mui-error)": {
                          color: "text.secondary",
                        },
                      }}
                    >
                      Target Spectrum
                    </FormLabel>
                    <RadioGroup
                      {...field}
                      aria-labelledby="specTarget-radio-buttons-group-label"
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      value={String(field.value)}
                    >
                      <Grid container>
                        <Grid size={{ xs: 4 }}>
                          <FormControlLabel
                            value="0"
                            control={<Radio size="small" />}
                            label="Flat"
                          />
                        </Grid>
                        <Grid size={{ xs: 4 }}>
                          <FormControlLabel
                            value="1"
                            control={<Radio size="small" />}
                            label="Whisper"
                          />
                        </Grid>
                        <Grid size={{ xs: 4 }}>
                          <FormControlLabel
                            value="2"
                            control={<Radio size="small" />}
                            label="Relaxed"
                          />
                        </Grid>
                        <Grid size={{ xs: 4 }}>
                          <FormControlLabel
                            value="3"
                            control={<Radio size="small" />}
                            label="Normal"
                          />
                        </Grid>
                        <Grid size={{ xs: 4 }}>
                          <FormControlLabel
                            value="4"
                            control={<Radio size="small" />}
                            label="Raised"
                          />
                        </Grid>
                        <Grid size={{ xs: 4 }}>
                          <FormControlLabel
                            value="5"
                            control={<Radio size="small" />}
                            label="Very Loud"
                          />
                        </Grid>
                      </Grid>
                    </RadioGroup>
                    <FormHelperText>
                      {errors.specTarget
                        ? errors.specTarget.message
                        : "Please select an option."}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button
            disabled={!isDirty}
            onClick={() => rhfReset()}
            variant="outlined"
          >
            Reset to Default
          </Button>
          <Button disabled={!isValid} type="submit" variant="contained">
            Run
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
